import { SensorLockerStatus } from '@frontend/device/types';
import { CommonMessage } from '@frontend/lang';
import { Slot } from '@frontend/slot/types';
import { useEffect, useMemo, useState } from 'react';
import { LuDoorClosed, LuDoorOpen, LuTriangleAlert } from 'react-icons/lu';

const LargeSlotCard = ({ slot, status }: { slot: Slot; status: SensorLockerStatus | undefined }) => {
    const [timerExpired, setTimerExpired] = useState(false);

    useEffect(() => {
        if (status === SensorLockerStatus.OPENED) {
            const timeoutId = setTimeout(() => {
                setTimerExpired(true);
            }, 30000);

            return () => clearTimeout(timeoutId);
        } else {
            setTimerExpired(false);
        }
    }, [status]);

    const { Icon, message, color } = useMemo(() => {
        const color = getColorByStatus(status, timerExpired);
        const Icon = getIconByStatus(status);
        const message = getStatusMessage(status, timerExpired);
        return { Icon, message, color };
    }, [status, timerExpired]);

    return (
        <div className='bg-background-surface border-border-default inline-flex items-center justify-between self-stretch rounded-xl border py-3 pr-4 pl-3'>
            <div className='flex items-center justify-start gap-3'>
                <div className={`${color} flex size-12 items-center justify-center rounded-full`}>
                    <Icon className='text-background-surface size-6' />
                </div>
                <div className='inline-flex flex-col items-start justify-start gap-0.5'>
                    <div className='text-brand-muted justify-start text-base font-medium'>{CommonMessage.OBJECTS.SLOT.DEFAULT}</div>
                    <div className='text-foreground-default justify-start text-base font-medium'>{slot.display}</div>
                </div>
            </div>
            {message && (
                <div
                    data-show-icon='false'
                    data-variant='Info'
                    className={`px-2 py-1 ${color} flex items-start justify-center gap-1 rounded-full`}>
                    <div className='text-background-surface justify-start text-center text-sm font-semibold'>{message}</div>
                </div>
            )}
        </div>
    );
};
export default LargeSlotCard;

const getStatusMessage = (status: SensorLockerStatus | undefined, timerExpired?: boolean): React.ReactNode | null => {
    switch (status) {
        case SensorLockerStatus.CLOSED:
            return CommonMessage.OBJECTS.SLOT.STATUS.CLOSED;
        case SensorLockerStatus.OPENED:
            if (timerExpired === true) return CommonMessage.OBJECTS.SLOT.STATUS.STILL_OPEN;
            return CommonMessage.OBJECTS.SLOT.STATUS.OPEN;
        case SensorLockerStatus.OPENING:
            return <>{CommonMessage.OBJECTS.SLOT.STATUS.OPENING}...</>;
        case SensorLockerStatus.NOT_CLOSED:
            return CommonMessage.OBJECTS.SLOT.STATUS.NOT_CLOSED;
        case SensorLockerStatus.NOT_OPENED:
            return CommonMessage.OBJECTS.SLOT.STATUS.NOT_OPENED;
        case SensorLockerStatus.ERROR:
        default:
            return null;
    }
};

const getColorByStatus = (status: SensorLockerStatus | undefined, timerExpired?: boolean): string => {
    switch (status) {
        case SensorLockerStatus.CLOSED:
            return 'bg-brand-muted';
        case SensorLockerStatus.OPENED:
            if (timerExpired === true) return 'bg-status-warning';
            return 'bg-status-info';
        case SensorLockerStatus.OPENING:
            return 'bg-status-info';
        case SensorLockerStatus.NOT_CLOSED:
            return 'bg-status-warning';
        case SensorLockerStatus.ERROR:
        case SensorLockerStatus.NOT_OPENED:
            return 'bg-status-error';
        default:
            return 'bg-status-info';
    }
};

const getIconByStatus = (status: SensorLockerStatus | undefined) => {
    switch (status) {
        case SensorLockerStatus.NOT_OPENED:
        case SensorLockerStatus.CLOSED:
            return LuDoorClosed;
        case SensorLockerStatus.OPENING:
        case SensorLockerStatus.NOT_CLOSED:
        case SensorLockerStatus.OPENED:
            return LuDoorOpen;
        case SensorLockerStatus.ERROR:
            return LuTriangleAlert;
        default:
            return () => null;
    }
};
